// React
import type { RouteRecord } from 'vite-react-ssg';
import { Navigate, type LoaderFunction } from 'react-router-dom';

// Services
import { getPages } from '@/services/page/pageService';
import { getOffers } from '@/services/offer/offerService';
import { getLandings } from '@/services/landing/landingService';
import { getClients } from '@/services/client/clientService';
import { getExpertises } from '@/services/expertise/expertiseService';
import { getTechnos } from '@/services/techno/technoService';
import { getArticleSlugs } from '@/services/articles/articlesService';

// Views
// - Main
import App from '@/App';
import Home, { homeLoader } from '@/views/Home/Home';

// - Pages
import Offer, { offerLoader } from '@/views/Offer/Offer';
import Page, { pageLoader } from '@/views/Page/Page';
import Landing, { landingLoader } from '@/views/Landing/Landing';
import Client, { clientLoader } from '@/views/Client/Client';
import Expertise, { expertiseLoader } from '@/views/Expertise/Expertise';
import Techno, { technoLoader } from '@/views/Techno/Techno';
import Blog, { blogListingLoader } from '@/views/Blog/Blog';
import Article, { articleLoader } from '@/views/Blog/Article';
import Contact, { contactLoader } from '@/views/Contact/Contact';

// - Error
import ErrorFallback from '@/views/ErrorFallback';
import Error404 from '@/views/Error404';
import { RoutesEnum } from '@/enum/RoutesEnum';

// import ProtectedRoute from './ProtectedRoute';

const getChildren = async (): Promise<RouteRecord[]> => {
  const [pages, offers, landings, clients, expertises, technos, articles] = await Promise.all([
    getPages(),
    getOffers(),
    getLandings(),
    getClients(),
    getExpertises(),
    getTechnos(),
    getArticleSlugs(),
  ]);

  const pagesRoutes = pages.map((page) => ({
    path: page.slug,
    Component: () => <Page />,
    loader: pageLoader,
  }));

  // Helper function to create nested routes
  const createNestedRoute = (
    path: string,
    items: { slug: string }[],
    Component: React.ComponentType,
    loader: LoaderFunction,
  ) => ({
    path,
    children: items.map((item) => ({
      path: item.slug,
      Component: () => <Component />,
      loader,
    })),
  });

  const nestedRoutes = [
    createNestedRoute(RoutesEnum.OFFERS, offers, Offer, offerLoader),
    createNestedRoute(RoutesEnum.LANDINGS, landings, Landing, landingLoader),
    createNestedRoute(RoutesEnum.CLIENTS, clients, Client, clientLoader),
    createNestedRoute(RoutesEnum.EXPERTISES, expertises, Expertise, expertiseLoader),
    createNestedRoute(RoutesEnum.TECHNOS, technos, Techno, technoLoader),
    {
      path: RoutesEnum.BLOG,
      children: [
        {
          index: true,
          Component: () => <Blog />,
          loader: blogListingLoader,
        },
        ...articles.map((article) => ({
          path: article.slug,
          Component: () => <Article />,
          loader: articleLoader,
        })),
      ],
    },
  ];

  return [...pagesRoutes, ...nestedRoutes];
};

const publicRoutes: RouteRecord[] = [
  {
    // id: 'root',
    path: '/',
    Component: App,
    // entry: 'src/App.tsx',
    errorElement: <Navigate to="/404" replace />,
    children: [
      {
        index: true,
        Component: () => <Home />,
        loader: homeLoader,
        errorElement: <ErrorFallback />,
      },
      {
        path: RoutesEnum.CONTACT,
        Component: () => <Contact />,
        loader: contactLoader,
      },
      ...(await getChildren()),
    ],
  },
  {
    path: '/404',
    Component: App,
    children: [
      {
        index: true,
        Component: () => <Error404 />,
        // loader: errorLoader
      },
    ],
  },
];

export default [...publicRoutes];
